import { compact, max, sortBy, uniqBy } from "lodash";
import moment from "moment";
import {
  ActiveReviewsComplianceProgramFragment,
  ActiveReviewsProgramAssessmentTemplateFragment,
  AssessmentDeliveryState,
  AssessmentNominationFragment,
  AssessmentState,
  AssessmentType,
  ComplianceProgramActionAssessmentDeliveryFragment,
  ComplianceProgramAppliesTo,
  ComplianceProgramCalibrationType,
  EngagementSurveyComplianceProgramFragment,
  GetComplianceProgramActionsQuery,
  UnblockedBy,
  UnmetComplainceProgramAssessmentTemplateFragment,
  UnmetComplianceProgramFragment,
  UserActivityComplianceGroupAssessmentTemplateFragment,
  UserActivityComplianceProgramMeetingFragment,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import { UseLabelType, getLabel } from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import { ButtonProps } from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
  assertNonNull,
  getUrlUsingTypename,
  removeCurrentYear,
} from "@helpers/helpers";
import { pluralize } from "@helpers/string";

import { getBlockedStepText } from "./components/compliance-program-progress-constants";

/**
 * What the backend says when a review's participant rule matches nobody and it
 * cannot be published. Matched by `errorMatches`, so it must stay verbatim.
 */
export const NO_PARTICIPANTS_ERROR =
  // eslint-disable-next-line local-rules/use-label
  "Cannot publish a review with no participants";

const appliesToLabels = (label: UseLabelType) => ({
  [ComplianceProgramAppliesTo.Organization]: "All employees",
  [ComplianceProgramAppliesTo.Departments]: `All employees in specific ${label(
    "team",
    {
      pluralize: true,
    },
  )}`,
  [ComplianceProgramAppliesTo.ReportsTo]:
    "All employees that report to specific managers",
  [ComplianceProgramAppliesTo.Managers]: "All managers",
  [ComplianceProgramAppliesTo.Ics]: "All individual contributors (ICs)",
  [ComplianceProgramAppliesTo.Users]: "Specific employees",
  [ComplianceProgramAppliesTo.CreatorDirectReports]: "My direct reports",
  [ComplianceProgramAppliesTo.CreatorManagementTree]: "My reporting tree",
});

export const getAppliesToLabel = (
  label: UseLabelType,
  appliesTo: ComplianceProgramAppliesTo,
  creator?: BasicUser | null,
  currentUserId?: number,
) => {
  if (appliesTo === ComplianceProgramAppliesTo.CreatorDirectReports) {
    return `${
      creator?.id === currentUserId ? "All my" : `All of ${creator?.name}'s`
    } direct reports`;
  }
  if (appliesTo === ComplianceProgramAppliesTo.CreatorManagementTree) {
    return `${
      creator?.id === currentUserId ? "All my" : `All of ${creator?.name}'s`
    } reports (full reporting tree)`;
  }
  return appliesToLabels(label)[appliesTo];
};

export const REVIEW_PROGRAM_BASE_PATH = "/assessments/program";
export const SURVEY_PROGRAM_BASE_PATH = "/survey/program";

/**
 * Direct link to a program page. Programs live in the Reviews section unless
 * they have an engagement survey template, in which case they live in the
 * Surveys section. Prefer this over the legacy `/programs/:id` path, which is
 * only a redirect (it costs an extra query before landing on the page).
 */
export const getProgramUrl = (
  programId: number,
  {
    isSurvey = false,
    subPath = "",
  }: { isSurvey?: boolean; subPath?: string } = {},
) =>
  `${isSurvey ? SURVEY_PROGRAM_BASE_PATH : REVIEW_PROGRAM_BASE_PATH}/${programId}${subPath}`;

export enum UserRelationEnum {
  manager,
  report,
}

const getReportsAndManagersWithMatchingOneonones = (
  complianceProgram:
    | NonNullable<GetComplianceProgramActionsQuery["complianceProgram"]>
    | NonNullable<ActiveReviewsComplianceProgramFragment>,
) => {
  const currentUser = currentUserVar();

  const matchingOneonones = assertEdgesNonNull(
    complianceProgram.matchingOneonones,
  );
  const reports = assertEdgesNonNull(complianceProgram.applicableReports).map(
    (user) => ({
      ...user,
      type: UserRelationEnum.report,
    }),
  );
  const managers = assertEdgesNonNull(complianceProgram.applicableManagers).map(
    (user) => ({
      ...user,
      type: UserRelationEnum.manager,
    }),
  );
  const reportsAndManagers = [...reports, ...managers];
  const items = reportsAndManagers.map((user) => {
    const userIds = sortBy([currentUser.id, user.id]);
    const matchingOneonone = matchingOneonones.find((oneonone) => {
      const participantIds = sortBy(
        assertEdgesNonNullWithStringId(oneonone.participants).map(
          (participant) => participant.user?.id,
        ),
      );
      return userIds.every((userId) => participantIds.includes(userId));
    });
    return { user, matchingOneonone };
  });
  return sortBy(items, (item) =>
    !item.matchingOneonone ? 0 : item.matchingOneonone.isFinalized ? 2 : 1,
  );
};

export type ComplianceProgramActionBasicItemProps = {
  key: number | string;
  id?: null | number | string;
  user?: BasicUser | null;
  label?: string | null;
  buttonProps: ButtonProps;
  statusText?: string | null;
  statusCompleted?: boolean | null;
  // The task exists but cannot be opened yet (e.g. a manager review waiting for pre-calibration).
  blocked?: boolean;
};

export type ComplianceProgramActionNominationItemProps =
  ComplianceProgramActionBasicItemProps & {
    assessmentOpenForNomination: AssessmentNominationFragment;
    complianceProgram?: AssessmentNominationFragment["complianceProgram"];
  };

export const mapAssessmentForNominationToItem = (
  currentUserId: number,
  assessmentOpenForNomination: AssessmentNominationFragment,
): ComplianceProgramActionNominationItemProps => ({
  key: assessmentOpenForNomination.id,
  id: assessmentOpenForNomination.id,
  complianceProgram: assessmentOpenForNomination.complianceProgram,
  user: assessmentOpenForNomination.targetUser,
  label:
    currentUserId === assessmentOpenForNomination.targetUser?.id
      ? "Peer Nominations"
      : null,
  buttonProps: {
    text: "Nominate peers",
    theme: buttonTheme.lightBlue,
  },
  statusText: `${assessmentOpenForNomination.nominationsCount} ${pluralize(
    "nomination",
    assessmentOpenForNomination.nominationsCount,
  )}`,
  statusCompleted: false,
  assessmentOpenForNomination: assessmentOpenForNomination,
});

export const mapAssessmentDeliveryToItem = (
  currentUserId: number,
  delivery: ComplianceProgramActionAssessmentDeliveryFragment,
) => {
  const canDeliver =
    delivery.state === AssessmentDeliveryState.Draft &&
    delivery.canUpdate?.permission;
  const total =
    max([delivery.peerRespondersCount, delivery.nominees.totalCount]) || 0;
  const label = getLabel();
  return {
    key: delivery.id,
    id: delivery.id,
    user: delivery.target,
    complianceProgram: delivery.complianceProgram,
    label:
      currentUserId === delivery.target?.id
        ? `${label("review")} package`
        : null,
    buttonProps: {
      text: canDeliver ? "Deliver" : `View`,
      to: getUrlUsingTypename(delivery),
      theme: canDeliver ? buttonTheme.lightBlue : undefined,
    },
    statusText:
      delivery.state === AssessmentDeliveryState.Draft &&
      delivery.peerRespondersCount &&
      delivery.peerRespondersCount > 0
        ? `${delivery.peerRespondersCount}/${total} ${pluralize(
            "peer",
            total,
          )} submitted`
        : delivery.state !== AssessmentDeliveryState.Draft
          ? `Delivered ${
              delivery.deliveryDatetime
                ? removeCurrentYear(
                    moment(delivery.deliveryDatetime).format("MMM DD, YYYY"),
                  )
                : ""
            }`
          : "",
    statusCompleted: delivery.state === AssessmentDeliveryState.Delivered,
  };
};

const getAssessmentStateList = (
  complianceProgram:
    | NonNullable<GetComplianceProgramActionsQuery["complianceProgram"]>
    | UnmetComplianceProgramFragment
    | ActiveReviewsComplianceProgramFragment,
  assessmentTemplate:
    | UserActivityComplianceGroupAssessmentTemplateFragment
    | UnmetComplainceProgramAssessmentTemplateFragment
    | ActiveReviewsProgramAssessmentTemplateFragment,
  currentUserId: number,
) => {
  const usersMissingAssessments = complianceProgram?.usersMissingAssessment
    ? complianceProgram.usersMissingAssessment.edges.map((edge) =>
        assertNonNull(edge?.node),
      )
    : [];
  const complianceGroupAssessments = complianceProgram?.assessments
    ? assertEdgesNonNull(complianceProgram.assessments)
    : [];
  const matchingUserMissingAssessments = usersMissingAssessments
    .filter((missingAssessment) => {
      return (
        missingAssessment.responder.id === currentUserId &&
        missingAssessment.assessmentTemplateId === assessmentTemplate.id
      );
    })
    .map((missingAssessment) => ({
      id: null,
      responder: missingAssessment.responder,
      target: missingAssessment.target,
      state: null,
      submittedDatetime: null,
      assessmentDeliveryState: null,
      dueDate: missingAssessment.dueDate,
      // Only the home page query asks for blocked reviews (`includeBlocked: true`).
      blocked: "blocked" in missingAssessment && missingAssessment.blocked,
      unblockedBy:
        "unblockedBy" in missingAssessment
          ? missingAssessment.unblockedBy
          : null,
      otherManagerReviewers: null as ReadonlyArray<OtherManagerReviewer> | null,
    }));
  const createdAssessments = complianceGroupAssessments
    .filter(
      (createdAssessment) =>
        createdAssessment.template.id === assessmentTemplate.id,
    )
    .filter((createdAssessment) => {
      // Engagement surveys submissions are not shown because they are delivered right away.
      if (
        assessmentTemplate.assessmentType === AssessmentType.EngagementSurvey
      ) {
        return false;
      }

      // A draft about somebody excused from the review, or who has left, is nobody's task
      // any more: the review asks nothing about them. Submitted work stays, as finished work.
      if (
        createdAssessment.state === AssessmentState.Draft &&
        createdAssessment.subjectLeftOut
      ) {
        return false;
      }

      // We show only existing submissions if none have been delivered in the same program.
      const isCurrentAssessmentDelivered =
        createdAssessment.assessmentDeliveryState ===
        AssessmentDeliveryState.Delivered;
      const hasOtherAssessmentsDelivered = matchingUserMissingAssessments.some(
        (missingAssessment) =>
          missingAssessment.target?.id === createdAssessment.target?.id &&
          missingAssessment.assessmentDeliveryState ===
            AssessmentDeliveryState.Delivered,
      );
      return !isCurrentAssessmentDelivered && !hasOtherAssessmentsDelivered;
    })
    .map((createdAssessment) => {
      // A draft stays in usersMissingAssessment until it is submitted. If its pair is
      // blocked again (calibration was undone), the draft waits like a new review.
      const missingAssessment = matchingUserMissingAssessments.find(
        (missing) => missing.target?.id === createdAssessment.target?.id,
      );
      const blocked =
        createdAssessment.state === AssessmentState.Draft &&
        !!missingAssessment?.blocked;
      // Only the home page and program page queries ask for it. `assertEdgesNonNull`
      // loses the union of query types, so TypeScript reads the field as unknown here.
      const otherManagerReviewers =
        "otherManagerReviewers" in createdAssessment
          ? (createdAssessment.otherManagerReviewers as ReadonlyArray<OtherManagerReviewer>)
          : null;
      return {
        id: createdAssessment.id,
        target: createdAssessment.target,
        responder: createdAssessment.responder,
        state: createdAssessment.state,
        submittedDatetime: createdAssessment.submittedDatetime,
        assessmentDeliveryState: createdAssessment.assessmentDeliveryState,
        dueDate: createdAssessment.dueDate,
        blocked,
        unblockedBy: blocked ? (missingAssessment?.unblockedBy ?? null) : null,
        otherManagerReviewers,
      };
    });
  const allAssessments = uniqBy(
    [...createdAssessments, ...matchingUserMissingAssessments],
    (assessment) => assessment.target?.id,
  );
  return sortBy(allAssessments, (assessment) =>
    assessment.state === null
      ? 1
      : assessment.state === AssessmentState.Draft
        ? 2
        : 3,
  );
};

export type OtherManagerReviewer = {
  readonly user: BasicUser;
  readonly submittedDatetime?: string | null;
};

type MissingAssessmentPair = {
  readonly responder: BasicUser;
  readonly target?: { readonly id: number } | null;
  readonly assessmentTemplateId?: number | null;
};

// A person with several managers is owed one manager review, and every manager is asked.
// These are the other managers still asked for the same person, so the viewer can
// coordinate with them. The first submission completes the review for everybody.
export const getOtherManagerReviewers = (
  missingAssessments: ReadonlyArray<MissingAssessmentPair>,
  assessmentTemplateId: number,
  targetId: number,
  currentUserId: number,
): BasicUser[] =>
  uniqBy(
    missingAssessments
      .filter(
        (missing) =>
          missing.assessmentTemplateId === assessmentTemplateId &&
          missing.target?.id === targetId &&
          missing.responder.id !== currentUserId &&
          missing.responder.id !== targetId,
      )
      .map((missing) => missing.responder),
    (responder) => responder.id,
  );

export const formatNameList = (names: string[]) =>
  names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

export type ComplianceProgramActionAssessmentItemProps =
  ComplianceProgramActionBasicItemProps & {
    otherManagerReviewers?: ReadonlyArray<OtherManagerReviewer>;
    // Who can end a blocked review's wait, from where the reader sits, and that as a sentence.
    unblockedBy?: UnblockedBy | null;
    blockedText?: string | null;
    assessmentTemplateId: number;
    assessmentType: AssessmentType;
    assessmentState: AssessmentState | null;
    assessmentDeliveryState: AssessmentDeliveryState | null;
    complianceProgram:
      | NonNullable<GetComplianceProgramActionsQuery["complianceProgram"]>
      | UnmetComplianceProgramFragment
      | EngagementSurveyComplianceProgramFragment;
  };

export const getAssessmentItemsForTemplate = (
  complianceProgram:
    | NonNullable<GetComplianceProgramActionsQuery["complianceProgram"]>
    | UnmetComplianceProgramFragment
    | ActiveReviewsComplianceProgramFragment,
  assessmentTemplate:
    | UserActivityComplianceGroupAssessmentTemplateFragment
    | UnmetComplainceProgramAssessmentTemplateFragment
    | ActiveReviewsProgramAssessmentTemplateFragment,
  currentUserId: number,
): ComplianceProgramActionAssessmentItemProps[] => {
  const label = getLabel();
  const isEngagementSurvey =
    assessmentTemplate.assessmentType === AssessmentType.EngagementSurvey;
  const path = isEngagementSurvey ? `/survey` : `/assessments`;
  const missingAssessments = complianceProgram?.usersMissingAssessment
    ? complianceProgram.usersMissingAssessment.edges.map((edge) =>
        assertNonNull(edge?.node),
      )
    : [];
  return compact(
    getAssessmentStateList(
      complianceProgram,
      assessmentTemplate,
      currentUserId,
    ).map((assessmentState) => {
      if (!assessmentState.target && !isEngagementSurvey) {
        return null;
      }
      const isCurrentUser = assessmentState.target?.id === currentUserId;
      // A started draft uses the backend list, which also says who already submitted.
      // The missing pairs cannot say it: one submission removes the target from them.
      const otherManagerReviewers =
        assessmentTemplate.assessmentType === AssessmentType.Performance &&
        assessmentState.target &&
        !isCurrentUser &&
        assessmentState.state !== AssessmentState.Submitted
          ? (assessmentState.otherManagerReviewers ??
            getOtherManagerReviewers(
              missingAssessments,
              assessmentTemplate.id,
              assessmentState.target.id,
              currentUserId,
            ).map((user) => ({ user, submittedDatetime: null })))
          : [];
      return {
        id: assessmentState.id,
        otherManagerReviewers,
        key: `${assessmentState.id}-${assessmentState.target?.id}-${assessmentTemplate.id}`,
        assessmentTemplateId: assessmentTemplate.id,
        assessmentType: assessmentTemplate.assessmentType,
        assessmentState: assessmentState.state,
        assessmentDeliveryState:
          assessmentState.assessmentDeliveryState || null,
        complianceProgram: complianceProgram,
        user: isEngagementSurvey
          ? assessmentState.responder
          : assessmentState.target,
        label: isCurrentUser
          ? isEngagementSurvey
            ? "Survey"
            : `Self ${label("review")}${
                assessmentTemplate.assessmentType === AssessmentType.Performance
                  ? ` for performance`
                  : assessmentTemplate.assessmentType === AssessmentType.Manager
                    ? ` for upward feedback`
                    : ""
              }`
          : null,
        buttonProps:
          assessmentState.state === AssessmentState.Draft ||
          assessmentState.state === null
            ? {
                text: `${
                  assessmentState.state === AssessmentState.Draft
                    ? "Continue"
                    : `Start`
                  // eslint-disable-next-line local-rules/use-label
                } ${label(isEngagementSurvey ? "survey" : "review")}`,
                to: `${path}/assessments/${assessmentState.id}`,
                theme: buttonTheme.lightBlue,
              }
            : {
                // assumed it is submitted
                text: `View`,
                to: `${path}/assessments/${assessmentState.id}`,
              },
        blocked: assessmentState.blocked,
        unblockedBy: assessmentState.unblockedBy,
        // The same sentence as a Blocked cell on the Progress tab: who can end the wait.
        blockedText:
          assessmentState.blocked && assessmentState.target
            ? getBlockedStepText(
                {
                  blockedReason: "Waiting for pre-calibration",
                  unblockedBy: assessmentState.unblockedBy,
                },
                assessmentState.target.name,
              )
            : null,
        statusText: assessmentState.blocked
          ? "Waiting for pre-calibration"
          : assessmentState.state === AssessmentState.Submitted &&
              assessmentState.submittedDatetime
            ? `Submitted ${removeCurrentYear(
                moment(assessmentState.submittedDatetime).format("MMM D, YYYY"),
              )}`
            : assessmentState.dueDate
              ? `Due ${removeCurrentYear(
                  moment(assessmentState.dueDate).format("MMM D, YYYY"),
                )}`
              : complianceProgram?.dueDate
                ? `Due ${removeCurrentYear(
                    moment(complianceProgram?.dueDate).format("MMM D, YYYY"),
                  )}`
                : null,
        statusCompleted:
          assessmentState.state === AssessmentState.Submitted &&
          !!assessmentState.submittedDatetime,
      };
    }),
  );
};

export type ComplianceProgramActionOneononeItemProps =
  ComplianceProgramActionBasicItemProps & {
    user: BasicUser;
    userRelation: number;
    requiredTopicTemplateId: number;
    matchingOneonone?: UserActivityComplianceProgramMeetingFragment;
    complianceProgram:
      | NonNullable<GetComplianceProgramActionsQuery["complianceProgram"]>
      | NonNullable<ActiveReviewsComplianceProgramFragment>;
  };

export const getOneononeItemsFromComplianceProgram = (
  complianceProgram:
    | NonNullable<GetComplianceProgramActionsQuery["complianceProgram"]>
    | NonNullable<ActiveReviewsComplianceProgramFragment>,
  currentUserId: number,
): ComplianceProgramActionOneononeItemProps[] => {
  const label = getLabel();
  const requiredTopicTemplates = complianceProgram?.requiredTopicTemplates
    ? assertEdgesNonNull(complianceProgram.requiredTopicTemplates)
    : [];
  const requiredTopicTemplate = requiredTopicTemplates[0];
  const reportsAndManagersWithMatchingOneonone = requiredTopicTemplate
    ? getReportsAndManagersWithMatchingOneonones(complianceProgram)
    : [];
  return reportsAndManagersWithMatchingOneonone.map(
    (reportAndManagerWithMatchingOneonone) => {
      return {
        key: reportAndManagerWithMatchingOneonone.user.id,
        id: reportAndManagerWithMatchingOneonone.user.id,
        user: reportAndManagerWithMatchingOneonone.user,
        requiredTopicTemplateId: requiredTopicTemplate.id,
        matchingOneonone: reportAndManagerWithMatchingOneonone.matchingOneonone,
        complianceProgram: complianceProgram,
        label:
          currentUserId === reportAndManagerWithMatchingOneonone.user.id &&
          reportAndManagerWithMatchingOneonone.user.type ===
            UserRelationEnum.report
            ? `${label("1-on-1", { pluralize: true })} Meetings`
            : null,
        buttonProps: reportAndManagerWithMatchingOneonone.matchingOneonone
          ? {
              text: "View Meeting",
            }
          : {
              text: `Schedule ${label("review")} Meeting`,
              theme: buttonTheme.lightBlue,
            },
        statusText: reportAndManagerWithMatchingOneonone.matchingOneonone
          ?.isFinalized
          ? "Finalized"
          : reportAndManagerWithMatchingOneonone.matchingOneonone?.draft
            ? "Draft"
            : reportAndManagerWithMatchingOneonone.matchingOneonone
                  ?.startDatetime
              ? "Scheduled"
              : null,
        statusCompleted:
          reportAndManagerWithMatchingOneonone.matchingOneonone?.isFinalized,
        userRelation: reportAndManagerWithMatchingOneonone.user.type,
      };
    },
  );
};

export type ComplianceProgramActionCalibrationGroupItemProps =
  ComplianceProgramActionBasicItemProps & {
    group: {
      id: number;
      name: string;
      complianceProgram: {
        id: number;
        title: string;
        calibration: ComplianceProgramCalibrationType;
        dueDate?: string | null;
        ongoing: boolean;
        state: any;
      };
    };
  };

export const mapCalibrationGroupsToItem = (
  calibrationGroups: NonNullable<
    GetComplianceProgramActionsQuery["calibrationGroups"]
  >,
): ComplianceProgramActionCalibrationGroupItemProps[] => {
  const groups = assertEdgesNonNull(calibrationGroups);

  return groups.map((group) => {
    return {
      key: group.id,
      id: group.id,
      user: null,
      label: null,
      buttonProps: {
        text: "View reporting",
        to: getProgramUrl(group.complianceProgram.id, {
          subPath: `/reporting/manager?calibrationGroupId=${group.id}`,
        }),
        theme: buttonTheme.lightBlue,
      },
      statusText: null,
      statusCompleted: false, // Backend now filters, so all shown groups are incomplete
      group,
    };
  });
};

/**
 * Why one person was left out of a send. The server writes these into the event's
 * payload, from a closed set it shares with the Timeline.
 */
const skipReasonLabels: Record<string, string> = {
  no_manager: "no manager",
  opted_out: "opted out",
  not_applicable: "not applicable",
  deactivated: "deactivated",
  chat_disabled: "chat not available",
  already_reminded: "already reminded",
  not_local_send_hour: "not their send hour",
};

/**
 * The payload of a review event, as an object.
 *
 * It arrives as a JSON *string*, because the server sends it as a JSONString scalar.
 * Reading a field straight off it finds nothing, so it has to be parsed first.
 */
const readEventPayload = (payload: unknown): Record<string, unknown> => {
  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return payload && typeof payload === "object"
    ? (payload as Record<string, unknown>)
    : {};
};

/**
 * The dates of the cycle a recurrence created.
 *
 * Not its title: the copy is made with the same title as the review the row sits on, so
 * naming it would only repeat the heading. The dates are the part that is new.
 */
export const getRecurredDates = (payload: unknown) => {
  const { start_date: startDate, due_date: dueDate } =
    readEventPayload(payload);
  if (typeof startDate !== "string" || typeof dueDate !== "string") {
    return null;
  }
  return `${moment(startDate).format("MMM D, YYYY")} to ${moment(dueDate).format("MMM D, YYYY")}`;
};

/** Why one person was left out of a send, in the words the Activity tab uses. */
export const getSkipReason = (payload: unknown) => {
  const reason = readEventPayload(payload).reason;
  return typeof reason === "string" && reason
    ? (skipReasonLabels[reason] ?? reason)
    : null;
};

/** Closed, safe coordinator outcomes; never render arbitrary payload text. */
export const getCoordinatorOutcome = (payload: unknown) => {
  const outcome = readEventPayload(payload).outcome;
  const labels: Record<string, string> = {
    pending: "Planned",
    executing: "In progress",
    succeeded: "Sent",
    failed: "Failed",
    unknown: "Delivery outcome unknown",
    cancelled: "Cancelled",
    suppressed: "Deferred",
  };
  return typeof outcome === "string" ? (labels[outcome] ?? null) : null;
};
